import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { appeler, ErreurApi } from "../api/client";
import { useApi } from "../api/hooks";
import { useSession } from "../auth/Session";
import { EtatChargement, EtatErreur } from "../composants/Avis";
import { Tuiles } from "../composants/Tableau";
import { DialogueAcces } from "./DialogueAcces";

const DossierSchema = z.object({
  id: z.string(),
  raisonSociale: z.string(),
  contact: z.string().nullable(),
  telephone: z.string().nullable(),
  email: z.string().nullable(),
  actif: z.boolean(),
  regime: z.string(),
  modeTenue: z.string(),
  sourceEcritures: z.string(),
  modules: z.array(z.string()),
  estDemonstration: z.boolean(),
  iaAutorisee: z.boolean(),
  /** NonActive | Active | Desactive — trois états, pas un booléen. */
  etatActivation: z.enum(["NonActive", "Active", "Desactive"]),
  activeLe: z.string().nullable(),
  aJetonDAcces: z.boolean(),
  /** L'identifiant du portail, ou nul si le client n'a pas encore d'acces. */
  identifiantPortail: z.string().nullable(),
  nbPieces: z.number(),
  derniereActivite: z.string().nullable(),
});

type Dossier = z.infer<typeof DossierSchema>;

const ActivationSchema = z.object({
  etatActivation: z.string(),
  activeLe: z.string().nullable().optional(),
  jetonAcces: z.string().optional(),
  lien: z.string().optional(),
  note: z.string().optional(),
});

const LibelleEtat: Record<Dossier["etatActivation"], string> = {
  NonActive: "Non activé",
  Active: "Activé",
  Desactive: "Désactivé",
};

/** Un instant relatif, plus lisible qu'une date brute dans une liste. */
function depuis(iso: string | null): string {
  if (iso === null) return "—";
  const jours = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return "hier";
  if (jours < 31) return `il y a ${jours} j`;
  if (jours < 365) return `il y a ${Math.floor(jours / 30)} mois`;
  return `il y a ${Math.floor(jours / 365)} an(s)`;
}

export function ListeDossiers() {
  const { session, deconnecter } = useSession();
  const qc = useQueryClient();
  const estGerant = session?.role === "admin";

  const [recherche, setRecherche] = useState("");
  const [filtre, setFiltre] = useState<"tous" | Dossier["etatActivation"]>("tous");
  const [lien, setLien] = useState<{ nom: string; lien: string } | null>(null);
  const [acces, setAcces] = useState<Dossier | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  const q = useApi(["dossiers"], "/dossiers", z.array(DossierSchema));

  const tous = useMemo(() => q.data ?? [], [q.data]);

  const dossiers = tous
    .filter((d) => filtre === "tous" || d.etatActivation === filtre)
    .filter((d) =>
      recherche === ""
        ? true
        : `${d.raisonSociale} ${d.email ?? ""} ${d.contact ?? ""}`
            .toLowerCase()
            .includes(recherche.toLowerCase()),
    );

  async function agir(d: Dossier, action: "activer" | "desactiver" | "lien") {
    setErreur(null);
    setEnCours(d.id);

    try {
      const chemin =
        action === "lien" ? `/dossiers/${d.id}/lien` : `/dossiers/${d.id}/${action}`;

      const r = await appeler(chemin, ActivationSchema, { method: "POST" });

      // Le jeton n'est rendu QU'UNE FOIS : on l'affiche tant que l'écran vit.
      // ⛔ LE LIEN EST AFFICHE ENTIER, AVEC SON HOTE.
      //
      // L'API rend un chemin — « /depot/<jeton> » — et elle a raison : elle ne
      // connait pas le domaine public sous lequel le front est servi, et l'y
      // inscrire en dur casserait des qu'il change.
      //
      // Mais c'est un chemin qu'on COLLE DANS UN COURRIEL au client. Sans hote,
      // il ne mene nulle part : le client recevrait un lien mort et l'equipe ne
      // saurait pas pourquoi. L'hote se prend donc ici, ou il est connu de facon
      // certaine — c'est la page elle-meme qui le porte.
      if (r.lien) {
        setLien({
          nom: d.raisonSociale,
          lien: new URL(r.lien, window.location.origin).toString(),
        });
      }

      void qc.invalidateQueries({ queryKey: ["dossiers"] });
    } catch (e) {
      setErreur(e instanceof ErreurApi ? (e.motif ?? e.message) : "Action impossible.");
    } finally {
      setEnCours(null);
    }
  }

  if (q.isLoading) return <EtatChargement quoi="des dossiers" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  return (
    <main className="liste-dossiers">
      <header className="entete">
        <div className="entete__logo">MDC</div>
        <div>
          <h1>Espace collaborateurs</h1>
          <div className="entete__sous-titre">La Maison de la Conformité — back-office de traitement</div>
        </div>
        <div className="entete__moi">
          <b>{session?.nom}</b>
          {estGerant && "gérant"}
          {estGerant && (
            <Link className="bouton-creux" to="/configuration">Configuration</Link>
          )}
          <button type="button" onClick={deconnecter}>Se déconnecter</button>
        </div>
      </header>

      <div className="contenu">
        <Tuiles
          tuiles={[
            { titre: "Dossiers", valeur: tous.length },
            { titre: "Activés", valeur: tous.filter((d) => d.etatActivation === "Active").length },
            {
              titre: "Non activés",
              valeur: tous.filter((d) => d.etatActivation === "NonActive").length,
            },
            {
              titre: "Désactivés",
              valeur: tous.filter((d) => d.etatActivation === "Desactive").length,
            },
            {
              titre: "Traitement IA autorisé",
              valeur: tous.filter((d) => d.iaAutorisee).length,
              note: "interrupteur par dossier",
            },
          ]}
        />

        <div className="filtres">
          <label className="filtre">
            Rechercher
            <input
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="raison sociale, courriel, contact"
            />
          </label>

          <label className="filtre">
            État
            <select value={filtre} onChange={(e) => setFiltre(e.target.value as typeof filtre)}>
              <option value="tous">Tous</option>
              <option value="Active">Activés</option>
              <option value="NonActive">Non activés</option>
              <option value="Desactive">Désactivés</option>
            </select>
          </label>
        </div>

        {erreur && <p className="alerte" role="alert">{erreur}</p>}

        {avis && (
          <div className="lien-emis">
            <strong>Accès du portail</strong>
            <p className="note">{avis}</p>
            <button type="button" onClick={() => setAvis(null)}>Fermer</button>
          </div>
        )}

        {lien && (
          <div className="lien-emis">
            <strong>Lien d'accès de {lien.nom}</strong>
            <code>{lien.lien}</code>
            <button type="button" onClick={() => void navigator.clipboard.writeText(lien.lien)}>
              Copier
            </button>
            <button type="button" onClick={() => setLien(null)}>Fermer</button>
            <p className="note">
              Ce lien n'est affiché <strong>qu'une fois</strong> : il n'est conservé que haché.
              Le régénérer révoque l'ancien.
            </p>
          </div>
        )}

        <div className="tableau-cadre">
          <table className="tableau grille-dossiers">
            <thead>
              <tr>
                <th>Dossier</th>
                <th>État</th>
                <th>Régime</th>
                <th>Tenue</th>
                <th>Écritures</th>
                <th className="droite">Pièces</th>
                <th>Dernière activité</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dossiers.map((d) => (
                <tr key={d.id} className={d.etatActivation === "Desactive" ? "ligne-eteinte" : undefined}>
                  <td>
                    <div className="cellule-dossier">
                      {d.etatActivation === "NonActive" ? (
                        <span className="cellule-dossier__nom">{d.raisonSociale}</span>
                      ) : (
                        <Link className="cellule-dossier__nom" to={`/dossiers/${d.id}/tableau-de-bord`}>
                          {d.raisonSociale}
                        </Link>
                      )}
                      <span className="cellule-dossier__contact">
                        {d.email ?? d.contact ?? d.telephone ?? "—"}
                      </span>
                      {d.estDemonstration && (
                        <span className="jeton jeton--scelle">Démonstration</span>
                      )}
                    </div>
                  </td>

                  <td>
                    <span className={`badge badge--${d.etatActivation.toLowerCase()}`}>
                      {LibelleEtat[d.etatActivation]}
                    </span>
                  </td>

                  <td>{d.regime}</td>
                  <td>{d.modeTenue}</td>
                  <td title="Ce qui fait foi pour les écritures">{d.sourceEcritures}</td>
                  <td className="droite">{d.nbPieces}</td>
                  <td>{depuis(d.derniereActivite)}</td>

                  <td>
                    <div className="actions-ligne">
                      {d.etatActivation === "NonActive" ? (
                        <>
                          {estGerant && (
                            <button
                              type="button"
                              className="bouton-plein"
                              disabled={enCours === d.id}
                              onClick={() => void agir(d, "activer")}
                              title="Émet le lien d'accès et monte l'arborescence documentaire"
                            >
                              Activer
                            </button>
                          )}
                          <span className="note">aucun lien émis</span>
                        </>
                      ) : (
                        <>
                          <Link className="bouton-creux" to={`/dossiers/${d.id}/tableau-de-bord`}>
                            Ouvrir
                          </Link>

                          {estGerant && (
                            <button
                              type="button"
                              className="bouton-creux"
                              disabled={enCours === d.id}
                              onClick={() => void agir(d, "lien")}
                              title="Régénère le lien : l'ancien est révoqué"
                            >
                              Lien
                            </button>
                          )}

                          {/* La SEULE voie de réinitialisation d'un mot de passe :
                              réémettre un lien ne refait plus les identifiants. */}
                          {estGerant && (
                            <button
                              type="button"
                              className="bouton-creux"
                              onClick={() => setAcces(d)}
                              title={
                                d.identifiantPortail
                                  ? `Réinitialise le mot de passe de « ${d.identifiantPortail} »`
                                  : "Crée les identifiants du client, sans passer par un lien"
                              }
                            >
                              {d.identifiantPortail ? "Mot de passe" : "Créer accès"}
                            </button>
                          )}

                          {estGerant &&
                            (d.actif ? (
                              <button
                                type="button"
                                className="bouton-creux"
                                disabled={enCours === d.id}
                                onClick={() => void agir(d, "desactiver")}
                                title="Coupe le lien. Rien n'est perdu."
                              >
                                Désactiver
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="bouton-plein"
                                disabled={enCours === d.id}
                                onClick={() => void agir(d, "activer")}
                                title="Émet un nouveau lien d'accès"
                              >
                                Réactiver
                              </button>
                            ))}

                          {/* Le portail TEL QUE LE CLIENT LE VOIT.
                              Aucune usurpation : le collaborateur y entre avec
                              SON jeton et sous SON nom — toute action qu'il y
                              ferait porte sa signature au journal, pas celle du
                              client. */}
                          <Link
                            className="bouton-creux"
                            to={`/portail/apercu/${d.id}`}
                            title="Ouvre le portail de ce client, tel qu'il le voit"
                          >
                            Portail
                          </Link>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {dossiers.length === 0 && <p className="a-faire">Aucun dossier ne correspond.</p>}

        <p className="note">
          Créer un dossier ne donne aucun accès : c'est l'<strong>activation</strong> qui émet le
          lien et monte l'arborescence documentaire. Un dossier porteur de données ne se supprime
          pas — il se désactive, et tout ce qui a été déposé demeure.
        </p>
      </div>

      {acces && (
        <DialogueAcces
          key={acces.id}
          dossier={acces}
          onFerme={() => setAcces(null)}
          onFait={(note) => {
            setAcces(null);
            setAvis(note);
            void qc.invalidateQueries({ queryKey: ["dossiers"] });
          }}
        />
      )}
    </main>
  );
}
